import ErrorFilledIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Error-Filled-Lg.js';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { Box, BoxProps, IconButton, Typography, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  atlasFontTextBody,
  atlasInverseGhostHoverSx,
  atlasRadiusLg,
  atlasSpacing,
  atlasToastAlertContainerSx,
  atlasToastAlertIconStripSx,
  atlasToastBodySx,
  type AtlasLensTheme,
} from '../Form/atlasLensStyles';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';

export type AtlasErrorToastProps = {
  message: string;
  onDismiss: () => void;
} & Omit<BoxProps, 'children'>;

/** Figma error toast — dark surface, red accent strip, message, dismiss control. */
export const AtlasErrorToast = forwardRef<HTMLDivElement, AtlasErrorToastProps>(
  function AtlasErrorToast({ message, onDismiss, ...rest }, ref) {
    const { t } = useTranslation();
    const theme = useTheme() as Theme & LensTheme;
    const atlasTheme = theme as AtlasLensTheme;
    const { tokens } = theme;
    const { toastSurface, accentRedRequired, typeInverse } = resolveAtlasLensColors(theme);
    const iconSize = atlasSpacing(atlasTheme, '3');
    const dismissButtonSize = tokens?.core?.spacing?.['5']?.value ?? '40px';

    return (
      <Box
        ref={ref}
        {...rest}
        role="alert"
        aria-live="assertive"
        data-qa-anchor="atlas-toast-error"
        sx={{
          ...atlasToastAlertContainerSx(atlasTheme),
          background: toastSurface,
        }}
      >
        <Box
          sx={{
            ...atlasToastAlertIconStripSx(atlasTheme),
            background: accentRedRequired,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              color: typeInverse,
              '& svg': {
                width: iconSize,
                height: iconSize,
              },
            }}
          >
            <ErrorFilledIcon />
          </Box>
        </Box>
        <Box sx={atlasToastBodySx(atlasTheme)}>
          <Typography
            sx={{
              ...atlasFontTextBody(atlasTheme),
              color: typeInverse,
              flex: 1,
            }}
          >
            {message}
          </Typography>
          <IconButton
            onClick={onDismiss}
            aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
            sx={{
              flexShrink: 0,
              width: dismissButtonSize,
              height: dismissButtonSize,
              p: atlasSpacing(atlasTheme, '1'),
              borderRadius: atlasRadiusLg(atlasTheme),
              color: typeInverse,
              ...atlasInverseGhostHoverSx(atlasTheme),
            }}
          >
            <CloseIcon style={{ fontSize: iconSize }} />
          </IconButton>
        </Box>
      </Box>
    );
  },
);
